import { useState } from "react";
import { blankTemplateCsv, parseCsv, parseXlsx, planImport, type ParsedImport } from "../domain/importing";
import { newId, useApp } from "../state/store";

export function ImportPanel({ primary = false, label = "选择 CSV / XLSX" }: { primary?: boolean; label?: string }) {
  const app = useApp();
  const [plan, setPlan] = useState<ParsedImport | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [message, setMessage] = useState("");

  return (
    <div className="stack">
      <div className="row-actions">
        <label className={primary ? "button primary" : "button"}>
          {label}
          <input
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              void readFile(file).then((table) => {
                try {
                  const next = planImport({
                    table,
                    now: new Date().toISOString(),
                    idFactory: newId,
                    snapshots: app.state.snapshots,
                    posts: app.state.postMetrics,
                  });
                  setPlan(next);
                  setMessage("");
                } catch (error) {
                  setPlan(null);
                  setMessage(error instanceof Error ? error.message : "这个文件读不了。");
                }
              });
            }}
          />
        </label>
        <button
          type="button"
          className="button"
          onClick={() => {
            const blob = new Blob([blankTemplateCsv()], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "mediadesk-metrics-template.csv";
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          下载空白模板
        </button>
      </div>
      <p className="muted">有标题或链接的行记为单条帖子，其余记为账号当日汇总。重复的日期或链接会先标出来。</p>
      {message ? <p className="inline-error">{message}</p> : null}
      {plan ? (
        <div className="stack">
          <p>
            可导入账号记录 {plan.snapshots.length} 条，帖子 {plan.posts.length} 条。
            {plan.duplicates.length ? ` 其中 ${plan.duplicates.length} 条和已有数据重复。` : ""}
          </p>
          {plan.errors.length ? (
            <ul className="error-list">
              {plan.errors.slice(0, 8).map((error) => (
                <li key={`${error.row}-${error.message}`}>
                  第 {error.row} 行：{error.message}
                </li>
              ))}
            </ul>
          ) : null}
          {plan.duplicates.length ? (
            <label className="check-line">
              <input type="checkbox" checked={overwrite} onChange={(event) => setOverwrite(event.target.checked)} />
              覆盖重复的日期或链接
            </label>
          ) : null}
          <button
            type="button"
            className="button line"
            onClick={() => {
              const result = app.importRecords(plan.snapshots, plan.posts, overwrite);
              app.notify(`已导入 ${result.saved} 条，跳过 ${result.skipped} 条。`);
              setPlan(null);
            }}
          >
            导入
          </button>
        </div>
      ) : null}
    </div>
  );
}

async function readFile(file: File) {
  if (file.name.toLowerCase().endsWith(".xlsx")) return parseXlsx(new Uint8Array(await file.arrayBuffer()));
  return parseCsv(await file.text());
}
